// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");

import React from "react";
import {Alert, Button, Card, Input, Space, Spin, Typography} from "antd";
import i18next from "i18next";
import {authConfig} from "./Auth";
import * as Setting from "../Setting";

function formatPasswordChangeExpiry(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

class ForcePasswordChangePage extends React.Component {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(window.location.search);
    this.state = {
      ticket: params.get("ticket") || "",
      code: params.get("code") || "",
      newPassword: "",
      confirmPassword: "",
      email: "",
      expiresAt: "",
      verified: false,
      loading: true,
      sending: false,
      error: "",
      notice: "",
      success: false,
    };
  }

  componentDidMount() {
    this.loadTicket();
  }

  request(path, fields = {}) {
    const body = new FormData();
    Object.keys(fields).forEach((key) => body.append(key, fields[key]));
    return fetch(`${authConfig.serverUrl}/api/force-password-change/${path}`, {
      method: "POST",
      credentials: "include",
      body,
      headers: {"Accept-Language": Setting.getAcceptLanguage()},
    }).then(res => res.json());
  }

  async loadTicket() {
    if (!this.state.ticket) {
      this.setState({loading: false, error: i18next.t("general:Password change ticket is missing")});
      return;
    }
    const res = await this.request("start", {ticket: this.state.ticket});
    if (res.status !== "ok") {
      this.setState({loading: false, error: res.msg});
      return;
    }
    this.setState({loading: false, email: res.data.email, expiresAt: formatPasswordChangeExpiry(res.data.expiresAt), verified: res.data.verified});
    if (!res.data.verified) {
      await this.sendCode();
    }
    if (this.state.code && !res.data.verified) {
      await this.verifyCode(this.state.code);
    }
  }

  async sendCode() {
    this.setState({sending: true, error: "", notice: ""});
    const res = await this.request("send-code", {ticket: this.state.ticket});
    this.setState({sending: false});
    if (res.status !== "ok") {
      this.setState({error: res.msg});
    } else {
      this.setState({notice: i18next.t("general:Password change verification email sent to your company email")});
    }
    return res;
  }

  async verifyCode(code = this.state.code) {
    if (!code) {
      this.setState({error: i18next.t("general:Please enter the email verification code")});
      return false;
    }
    const res = await this.request("verify-code", {ticket: this.state.ticket, code});
    if (res.status !== "ok") {
      this.setState({error: res.msg});
      return false;
    }
    this.setState({verified: true, code, error: ""});
    return true;
  }

  async submit() {
    if (this.state.newPassword !== this.state.confirmPassword) {
      this.setState({error: i18next.t("general:The passwords do not match")});
      return;
    }
    if (!this.state.verified && !(await this.verifyCode())) {
      return;
    }
    const res = await this.request("set-password", {
      ticket: this.state.ticket,
      code: this.state.code,
      newPassword: this.state.newPassword,
    });
    if (res.status !== "ok") {
      this.setState({error: res.msg});
      return;
    }
    this.setState({success: true});
    const signinUrl = sessionStorage.getItem("signinUrl");
    sessionStorage.removeItem("signinUrl");
    window.location.assign(signinUrl || res.data?.redirectUri || "/login");
  }

  render() {
    if (this.state.loading) {
      return <Spin size="large" />;
    }
    return (
      <Card title={i18next.t("general:Set a new password")} style={{maxWidth: 420, margin: "8vh auto"}}>
        <Space direction="vertical" size="middle" style={{width: "100%"}}>
          <Alert
            type="warning"
            showIcon={true}
            message={i18next.t("general:Detected that your password is still the initial password 123456. Please enter the code from your company email before setting a new password")}
          />
          {this.state.email ? <Typography.Text strong>{this.state.email}</Typography.Text> : null}
          {this.state.expiresAt ? <Typography.Text type="secondary">{i18next.t("general:Password change ticket expires at {{expiresAt}}", {expiresAt: this.state.expiresAt})}</Typography.Text> : null}
          {this.state.notice ? <Alert type="success" showIcon={true} message={this.state.notice} /> : null}
          {this.state.error ? <Alert type="error" message={this.state.error} /> : null}
          {this.state.success ? <Alert type="success" message={i18next.t("general:Password changed. Redirecting to sign in")} /> : null}
          {!this.state.verified ? <Input
            name="verification-code"
            autoComplete="one-time-code"
            inputMode="numeric"
            type="text"
            maxLength={6}
            spellCheck={false}
            autoCorrect="off"
            value={this.state.code}
            onChange={e => this.setState({code: e.target.value})}
            placeholder={i18next.t("general:Email verification code")}
          /> : null}
          {!this.state.verified ? <Button onClick={() => this.sendCode()} loading={this.state.sending}>{i18next.t("general:Resend verification email")}</Button> : null}
          <Input.Password name="new-password" autoComplete="new-password" value={this.state.newPassword} onChange={e => this.setState({newPassword: e.target.value})} placeholder={i18next.t("general:New password")} />
          <Input.Password name="confirm-new-password" autoComplete="new-password" value={this.state.confirmPassword} onChange={e => this.setState({confirmPassword: e.target.value})} placeholder={i18next.t("general:Confirm new password")} />
          <Button type="primary" block onClick={() => this.submit()}>{i18next.t("general:Set password")}</Button>
        </Space>
      </Card>
    );
  }
}

export default ForcePasswordChangePage;
